import React, { useState } from 'react';
import { FormField } from './FormField';
import { CalendarIcon, AlertCircleIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface PreAuthPaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
}
export function PreAuthPayment({
  onExpiryDateChange,
  onAmountChange,
  onCurrencyChange,
  onReferenceChange
}: PreAuthPaymentProps) {
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const handleAmountChange = (newAmount: string) => {
    if (onAmountChange) {
      onAmountChange(newAmount);
    }
  };
  const handleCurrencyChange = (newCurrency: string) => {
    if (onCurrencyChange) {
      onCurrencyChange(newCurrency);
    }
  };
  const handleReferenceChange = (newReference: string) => {
    if (onReferenceChange) {
      onReferenceChange(newReference);
    }
  };
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    if (onExpiryDateChange) {
      onExpiryDateChange(date);
    }
  };
  return <div className="space-y-6" data-id="element-1565">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1566">
        <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1567">
          Security hold payment
        </h2>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6 flex items-start" data-id="element-1568">
          <AlertCircleIcon size={18} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1569" />
          <p className="text-sm text-blue-800" data-id="element-1570">
            This will place a temporary hold on the customer's card without
            charging them. You can capture or release the funds later. Ideal for
            security deposits, hotel stays, or rentals.
          </p>
        </div>
        <FormField label="Hold amount" required tooltip="The amount to temporarily reserve on the customer's card" data-id="element-1571">
          <div className="relative" data-id="element-1572">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1573">
              <span className="text-gray-500" data-id="element-1574">$</span>
            </div>
            <input type="text" className="block w-full pl-8 pr-12 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" onChange={e => handleAmountChange(e.target.value)} data-id="element-1575" />
            <div className="absolute inset-y-0 right-0 flex items-center" data-id="element-1576">
              <select className="h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-500 sm:text-sm rounded-md focus:ring-blue-500 focus:border-blue-500" onChange={e => handleCurrencyChange(e.target.value)} data-id="element-1577">
                <option data-id="element-1578">USD</option>
                <option data-id="element-1579">EUR</option>
                <option data-id="element-1580">GBP</option>
              </select>
            </div>
          </div>
        </FormField>
        <FormField label="Reference" tooltip="A reference for this security hold" data-id="element-1581">
          <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Booking #1234 or Room 101" onChange={e => handleReferenceChange(e.target.value)} data-id="element-1582" />
        </FormField>
        <FormField label="Description" tooltip="A brief description of what this security hold is for" data-id="element-1583">
          <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Security deposit or Hotel stay" data-id="element-1584" />
        </FormField>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1585">
        <FormField label="Hold expiration" tooltip="When this security hold will expire" required data-id="element-1586">
          <div className="relative" data-id="element-1587">
            <input type="text" className="block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="MM/DD/YYYY" data-id="element-1588" />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1589">
              <CalendarIcon size={16} className="text-gray-400" data-id="element-1590" />
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-500" data-id="element-1591">
            Security holds typically expire after 7 days depending on the
            customer's bank
          </p>
        </FormField>
        <FormField label="Capture options" tooltip="How and when to charge the held funds" data-id="element-1592">
          <div className="space-y-2" data-id="element-1593">
            <label className="flex items-center" data-id="element-1594">
              <input type="radio" name="captureOption" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" defaultChecked data-id="element-1595" />
              <span className="ml-2 text-sm text-gray-700" data-id="element-1596">
                Manual capture (charge funds manually when needed)
              </span>
            </label>
            <label className="flex items-center" data-id="element-1597">
              <input type="radio" name="captureOption" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-1598" />
              <span className="ml-2 text-sm text-gray-700" data-id="element-1599">
                Automatically charge after
                <input type="number" className="mx-2 w-16 px-2 py-1 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm" placeholder="7" min="1" max="30" data-id="element-1600" />
                days
              </span>
            </label>
          </div>
        </FormField>
      </div>
      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} hideExpiry={true} data-id="element-1601" />
    </div>;
}